import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { SubscriptionTransferRequestSummary } from "models/subscription-transfer";
import { SubscriptionTransferRequestService } from "../../services/subscriptionTransferService";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import Chip from "@mui/material/Chip";
import RequestCard from "./display-components/RequestCard"; // Ensure the import path is correct
import StatusChip from "../general/StatusChip"; // Ensure the import path is correct

const subscriptionTransferService = new SubscriptionTransferRequestService();

const SubscriptionTransfers: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState<string>(
    generateCurrentMonthValue()
  );
  const [requests, setRequests] = useState<SubscriptionTransferRequestSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const months = generateMonths();

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      try {
        const data =
          await subscriptionTransferService.getSubscriptionTransferRequestsByMonth(
            selectedMonth,
          );
        setRequests(data);
      } catch (error) {
        console.error("Error fetching subscription transfer requests:", error);
        setRequests([]);
      }
      setLoading(false);
    };

    fetchRequests();
  }, [selectedMonth]);

  const handleMonthChange = (event: SelectChangeEvent) => {
    setSelectedMonth(event.target.value);
  };

  const groupRecordsByDate = (
    records: SubscriptionTransferRequestSummary[],
  ): { [date: string]: SubscriptionTransferRequestSummary[] } => {
    return records.reduce(
      (groups, record) => {
        const date = record.createdAt.substring(0, 10);
        if (!groups[date]) {
          groups[date] = [];
        }
        groups[date].push(record);
        return groups;
      },
      {} as { [date: string]: SubscriptionTransferRequestSummary[] },
    );
  };

  const groupedRecords = groupRecordsByDate(requests);

  const subscriptionIdFromInput = (input: SubscriptionTransferRequestSummary["input"]): string => {
    if (typeof input === "string") {
      try {
        const parsed = JSON.parse(input) as { subscriptionId?: string | number };
        return String(parsed.subscriptionId ?? "");
      } catch {
        return "";
      }
    }
    return String(input?.subscriptionId ?? "");
  };

  return (
    <Container sx={{ pt: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
        <IconButton
          onClick={() => navigate("/")}
          size="small"
          sx={{ alignSelf: "flex-start", flexGrow: 0 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1, textAlign: "center" }}>
          Subscription Transfer Requests
        </Typography>
        <Box sx={{ flexGrow: 0, width: "48px" }} />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
        <Typography variant="h6" mr={2}>
          Select Month:
        </Typography>
        <Select
          value={selectedMonth}
          onChange={handleMonthChange}
          displayEmpty
          inputProps={{ "aria-label": "Select Month" }}
        >
          {months.map((month) => (
            <MenuItem key={month.value} value={month.value}>
              {month.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Typography variant="h5" component="h2" gutterBottom>
        New Request
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
        <RequestCard
          icon={<BusinessCenterIcon fontSize="large" />}
          title="New Subscription Transfer"
          onClick={() => navigate("/subscription-transfers/ps-new-biz-form")}
        />
      </Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Subscription Transfer Records
      </Typography>
      {loading ? (
        <Typography>Loading...</Typography>
      ) : Object.keys(groupedRecords).length === 0 ? (
        <Typography>No subscription transfer requests found</Typography>
      ) : (
        <Box>
          {Object.keys(groupedRecords).map((date) => (
            <Box key={date} sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {new Date(date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>
              {groupedRecords[date].map((request) => (
                <Box
                  key={request.requestId}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1,
                    borderBottom: "1px solid #ccc",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                  onClick={() =>
                    navigate(`/subscription-transfers/${request.requestId}`)
                  }
                >
                  <Box sx={{ flex: "1 1 20%" }}>
                    <Typography variant="body1">{request.requestId}</Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: "1 1 20%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Chip
                      label={`Subscription ID: ${subscriptionIdFromInput(request.input)}`}
                      color="default"
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: "1 1 30%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Chip label={request.requester} color="default" />
                  </Box>
                  <Box
                    sx={{
                      flex: "1 1 15%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <StatusChip status={request.status} />
                  </Box>
                  <Box
                    sx={{
                      flex: "1 1 20%",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Typography variant="body2">
                      {new Date(request.createdAt).toLocaleString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                      })}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Container>
  );
};

// Helper function to generate the current month value in "YYYY-MM" format
const generateCurrentMonthValue = (): string => {
  return new Date().toISOString().substring(0, 7);
};

// Helper function to generate months from 08/2024 to the current month
const generateMonths = (): { value: string; label: string }[] => {
  const startMonth = new Date(2024, 7, 2, 12, 0, 0); // August 2024
  const currentMonth = new Date();
  const months = [];

  for (
    let dt = new Date(startMonth);
    dt <= currentMonth;
    dt.setMonth(dt.getMonth() + 1)
  ) {
    const value = dt.toISOString().substring(0, 7);
    const label = dt.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
    months.push({ value, label });
  }

  return months.reverse(); // Reverse to have the latest month first
};

export default SubscriptionTransfers;
